import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import OrderDesign from './OrderDesign';
import { OrderCardBubbles } from './components/OrderCardBubbles';
import { API_URL } from './config/apiConfig';
import { exploreUnknownRunFailureLabel } from './utils/exploreUnknownRunFailureLabel';

const RESOURCE_IMAGE_MAP = {
  lavender: '/images/resource_lavender.png',
  biomass: '/images/resource_biomass.png',
  food_rations: '/images/resource_foodrations.png',
  stone: '/images/resource_stone.png',
  erray: '/images/resource_erray.png',
  energy: '/images/powerplant.png',
};

function getResourceDisplayName(resourceType) {
  const nameMap = {
    lavender: 'Lavender',
    biomass: 'Biomass',
    food_rations: 'Food Rations',
    stone: 'Stone',
    erray: 'Erray',
    energy: 'Energy',
  };
  const key = (resourceType || '').toLowerCase();
  return nameMap[key] || resourceType;
}

const BUILDING_DISPLAY_NAMES = {
  powerplant: 'Powerplant',
  eclipse_array: 'Eclipse Array',
  eclipse_array_upgraded: 'Upgraded Eclipse Array',
  command_center: 'Command Center',
  cattle_ranch: 'Cattle Ranch',
  quarry: 'Quarry',
  sewers: 'Sewers',
  portal: 'Portal',
  lavender_field: 'Lavender Field',
  training_grounds: 'Training Grounds',
  barracks: 'Barracks',
  armory: 'Armory',
  drone_hangar: 'Drone Hangar',
  communications_hub: 'Communications Hub',
  intelligence_agency: 'Intelligence Agency',
  bank: 'Bank',
  research_center: 'Research Center',
  hospital: 'Hospital',
  hospital_upgraded: 'Upgraded Hospital',
  airfield: 'Airfield',
};

const BUILDING_IMAGES = {
  powerplant: '/images/powerplant.png',
  eclipse_array: '/images/array.png',
  eclipse_array_upgraded: '/images/array_upgraded.png',
  command_center: '/images/command.png',
  cattle_ranch: '/images/cattle.png',
  quarry: '/images/stone.png',
  sewers: '/images/sewers.png',
  portal: '/images/erray.png',
  lavender_field: '/images/lavender.png',
  training_grounds: '/images/training.png',
  barracks: '/images/barracks.png',
  armory: '/images/armory.png',
  drone_hangar: '/images/dronehangar.png',
  communications_hub: '/images/comhub.png',
  intelligence_agency: '/images/intel.png',
  bank: '/images/bank.png',
  research_center: '/images/research.png',
  hospital: '/images/hospital.png',
  hospital_upgraded: '/images/hospital_upgraded.png',
  airfield: '/images/airfield.png',
};

function ExploreUnknownRunStatisticsSection({ statistics }) {
  const { t, i18n } = useTranslation();
  const [factionTranslations, setFactionTranslations] = useState({});

  const getTranslatedFactionName = useCallback(
    (factionName) => {
      if (!factionName) {
        return factionName;
      }
      return factionTranslations[factionName] || factionName;
    },
    [factionTranslations]
  );

  useEffect(() => {
    let cancelled = false;

    const fetchFactionTranslations = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          if (!cancelled) {
            setFactionTranslations({});
          }
          return;
        }
        const currentLanguage = i18n.language || 'en-us';
        const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
        const response = await fetch(
          `${API_URL}/factions?lang=${encodeURIComponent(normalizedLanguage)}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (!response.ok) {
          throw new Error(`factions HTTP ${response.status}`);
        }
        const factions = await response.json();
        if (cancelled) {
          return;
        }
        const translationMap = {};
        factions.forEach((faction) => {
          translationMap[faction.english_name] = faction.name;
        });
        setFactionTranslations(translationMap);
      } catch (err) {
        console.error('ExploreUnknownRunStatisticsSection: faction translation fetch failed:', err);
        if (!cancelled) {
          setFactionTranslations({});
        }
      }
    };

    fetchFactionTranslations();
    return () => {
      cancelled = true;
    };
  }, [i18n.language]);

  if (!statistics || typeof statistics !== 'object') {
    throw new Error('ExploreUnknownRunStatisticsSection: statistics is required');
  }

  const otherCount = Number(statistics.otherPlayersReachedCount);
  if (!Number.isFinite(otherCount) || otherCount < 0) {
    throw new Error(
      `ExploreUnknownRunStatisticsSection: invalid otherPlayersReachedCount (${String(statistics.otherPlayersReachedCount)})`
    );
  }

  const endLabel = exploreUnknownRunFailureLabel(statistics, t, getTranslatedFactionName, {
    secondPerson: true,
  });
  const isHomecoming = statistics.endReason === 'complete';
  const peersLabel =
    otherCount === 0
      ? t('afterBattle.exploreUnknownRunStats.noOneCameThisFar')
      : t('afterBattle.exploreUnknownRunStats.othersCameThisFar', { count: otherCount });

  return (
    <section
      className="eu-run-rewards-section eu-run-rewards-stats-section"
      aria-label={t('afterBattle.exploreUnknownRunStats.title')}
    >
      <h3 className="eu-run-rewards-section-title">{t('afterBattle.exploreUnknownRunStats.title')}</h3>
      <p
        className={`eu-run-rewards-stat-line eu-run-rewards-stat-failure${
          isHomecoming
            ? ' eu-run-rewards-stat-failure--homecoming'
            : statistics.bossIntroTranslationKey
              ? ' eu-run-rewards-stat-failure--boss'
              : ''
        }`}
      >
        {endLabel}
      </p>
      <p className="eu-run-rewards-stat-line eu-run-rewards-stat-peers">{peersLabel}</p>
    </section>
  );
}

function ExploreUnknownRunRewardsOverview({ summary }) {
  const { t } = useTranslation();

  if (!summary || typeof summary !== 'object') {
    throw new Error('ExploreUnknownRunRewardsOverview: summary is required');
  }

  const statistics = summary.statistics;
  if (!statistics || typeof statistics !== 'object') {
    throw new Error('ExploreUnknownRunRewardsOverview: summary.statistics is required');
  }

  const resources = summary.resources && typeof summary.resources === 'object' ? summary.resources : {};
  const resourceEntries = Object.entries(resources).filter(([, amount]) => Number(amount) > 0);
  const billyBucks = Number(summary.billyBucks) || 0;
  const buildings = Array.isArray(summary.buildings) ? summary.buildings : [];
  const orders = Array.isArray(summary.orders) ? summary.orders : [];

  const hasAnyRewards =
    resourceEntries.length > 0 || billyBucks > 0 || buildings.length > 0 || orders.length > 0;

  return (
    <div className="eu-run-rewards-overview" role="region" aria-label={t('afterBattle.exploreUnknownRunRewardsTitle')}>
      <ExploreUnknownRunStatisticsSection statistics={statistics} />

      {!hasAnyRewards ? (
        <p className="eu-run-rewards-empty">{t('afterBattle.exploreUnknownRunRewardsEmpty')}</p>
      ) : (
        <>
          <h2 className="eu-run-rewards-heading">{t('afterBattle.exploreUnknownRunRewardsTitle')}</h2>

          {(resourceEntries.length > 0 || billyBucks > 0) && (
            <section className="eu-run-rewards-section">
              <h3 className="eu-run-rewards-section-title">
                {t('afterBattle.exploreUnknownRunResourcesTitle')}
              </h3>
              <div className="eu-run-rewards-resource-row">
                {resourceEntries.map(([resourceType, amount]) => {
                  const key = resourceType.toLowerCase();
                  const label = getResourceDisplayName(key);
                  const imgSrc = RESOURCE_IMAGE_MAP[key] || '/images/resource_biomass.png';
                  return (
                    <div key={key} className="eu-run-rewards-resource-tile">
                      <img src={imgSrc} alt={label} className="eu-run-rewards-resource-img" />
                      <span className="eu-run-rewards-resource-amount">{amount}</span>
                      <span className="eu-run-rewards-resource-label">{label}</span>
                    </div>
                  );
                })}
                {billyBucks > 0 && (
                  <div className="eu-run-rewards-resource-tile">
                    <img
                      src="/images/billy_bucks.png"
                      alt={t('afterBattle.exploreUnknownRunBillyBucks')}
                      className="eu-run-rewards-resource-img"
                    />
                    <span className="eu-run-rewards-resource-amount">{billyBucks}</span>
                    <span className="eu-run-rewards-resource-label">
                      {t('afterBattle.exploreUnknownRunBillyBucks')}
                    </span>
                  </div>
                )}
              </div>
            </section>
          )}

          {buildings.length > 0 && (
            <section className="eu-run-rewards-section">
              <h3 className="eu-run-rewards-section-title">
                {t('afterBattle.exploreUnknownRunBuildingsTitle')}
              </h3>
              <div className="eu-run-rewards-buildings-row">
                {buildings.map((b, index) => {
                  const buildingId = b.building_id || '';
                  const displayName =
                    BUILDING_DISPLAY_NAMES[buildingId] ||
                    buildingId.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                  const imgSrc =
                    BUILDING_IMAGES[buildingId] ||
                    (buildingId ? `/images/${buildingId}.png` : '/images/command.png');
                  return (
                    <div key={`${buildingId}-${index}`} className="eu-run-rewards-building-tile">
                      <img src={imgSrc} alt={displayName} className="eu-run-rewards-building-img" />
                      <span className="eu-run-rewards-building-label">{displayName}</span>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {orders.length > 0 && (
            <section className="eu-run-rewards-section eu-run-rewards-orders-section">
              <h3 className="eu-run-rewards-section-title">
                {t('afterBattle.exploreUnknownRunOrdersTitle')}
              </h3>
              <div className="strategy-orders-grid eu-run-rewards-orders-grid">
                {orders.map((order) => {
                  if (!order || order.id == null) return null;
                  const rarity = (order.rarity || 'common').toLowerCase();
                  return (
                    <div
                      key={order.id}
                      className={`reward-order-container rarity-${rarity}`}
                    >
                      <OrderDesign
                        order={order}
                        count={0}
                        minimal={false}
                        rarity={order.rarity}
                        lazyLoad={false}
                        gameState={null}
                      />
                      <OrderCardBubbles rarity={order.rarity || 'common'} />
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

export default ExploreUnknownRunRewardsOverview;
